import { useState, useEffect, useRef } from 'react';
import { X, Pencil, Save, Plus, Trash2, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useUpdateTestCase, useReviewComplete } from '../../hooks/useTestCases';
import type { TestCaseListItem } from '../../services/testCaseService';

const TYPE_OPTIONS = [
  { value: 'POSITIVE', label: 'Positive' },
  { value: 'NEGATIVE', label: 'Negative' },
  { value: 'BOUNDARY', label: 'Boundary' },
  { value: 'ALTERNATIVE', label: 'Alternative' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'BLOCKER', label: '블로커' },
  { value: 'CRITICAL', label: '긴급' },
  { value: 'HIGH', label: '높음' },
  { value: 'MEDIUM', label: '보통' },
  { value: 'LOW', label: '낮음' },
] as const;

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

interface Step {
  step: number;
  action: string;
}

interface Props {
  testCase: TestCaseListItem;
  projectId: string;
  onClose: () => void;
}

export default function TCDetailPanel({ testCase, projectId, onClose }: Props) {
  const updateMutation = useUpdateTestCase(projectId);
  const reviewMutation = useReviewComplete(projectId);
  const [isEditing, setIsEditing] = useState(false);

  // Edit form state
  const [title, setTitle] = useState(testCase.title);
  const [type, setType] = useState(testCase.type);
  const [priority, setPriority] = useState(testCase.priority);
  const [precondition, setPrecondition] = useState(testCase.precondition ?? '');
  const [steps, setSteps] = useState<Step[]>(
    testCase.steps?.length ? testCase.steps.map((s) => ({ step: s.step, action: s.action })) : [{ step: 1, action: '' }],
  );
  const [expectedResult, setExpectedResult] = useState(testCase.expectedResult ?? '');
  const stepInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset form when testCase changes
  useEffect(() => {
    setTitle(testCase.title);
    setType(testCase.type);
    setPriority(testCase.priority);
    setPrecondition(testCase.precondition ?? '');
    setSteps(
      testCase.steps?.length
        ? testCase.steps.map((s) => ({ step: s.step, action: s.action }))
        : [{ step: 1, action: '' }],
    );
    setExpectedResult(testCase.expectedResult ?? '');
    setIsEditing(false);
  }, [testCase]);

  const handleCancel = () => {
    setTitle(testCase.title);
    setType(testCase.type);
    setPriority(testCase.priority);
    setPrecondition(testCase.precondition ?? '');
    setSteps(
      testCase.steps?.length
        ? testCase.steps.map((s) => ({ step: s.step, action: s.action }))
        : [{ step: 1, action: '' }],
    );
    setExpectedResult(testCase.expectedResult ?? '');
    setIsEditing(false);
  };

  const addStep = () => {
    setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, step: i + 1 }));
    });
  };

  const updateStep = (index: number, value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, action: value } : s)));
  };

  const handleStepKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const nextIdx = idx + 1;
    if (nextIdx < steps.length && !steps[nextIdx].action.trim()) {
      // 다음 단계가 빈칸이면 포커스 이동
      stepInputRefs.current[nextIdx]?.focus();
    } else {
      // 새 단계 추가 후 포커스
      setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
      requestAnimationFrame(() => {
        stepInputRefs.current[nextIdx]?.focus();
      });
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        testCaseId: testCase.id,
        data: {
          title: title.trim(),
          type: type as 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE',
          priority: priority as 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
          precondition: precondition.trim() || null,
          steps: steps.map((s) => ({ step: s.step, action: s.action.trim() })),
          expectedResult: expectedResult.trim() || null,
        },
      });
      toast.success('테스트케이스가 수정되었습니다.');
      setIsEditing(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '수정에 실패했습니다.');
    }
  };

  const handleReviewComplete = async () => {
    try {
      await reviewMutation.mutateAsync(testCase.id);
      toast.success('검토 완료 처리되었습니다.');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (status === 403) {
        toast.error('검토완료 권한이 없습니다.');
      } else if (status === 400) {
        toast.error(msg || '처리할 수 없는 상태입니다.');
      } else {
        toast.error('검토완료 처리 중 오류가 발생했습니다.');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900">테스트케이스 상세</h3>
            <span className="font-mono text-sm text-gray-500">{testCase.tcId}</span>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                {testCase.needsReview && (
                  <button
                    onClick={handleReviewComplete}
                    disabled={reviewMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-300 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle className="size-3.5" />
                    {reviewMutation.isPending ? '처리 중...' : '검토완료'}
                  </button>
                )}
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Pencil className="size-3.5" />
                  수정
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="size-3.5" />
                  {updateMutation.isPending ? '저장 중...' : '저장'}
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="size-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Read-only fields */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="block text-sm font-medium text-gray-500 mb-1">TC ID</span>
              <span className="text-sm font-mono text-gray-900">{testCase.tcId}</span>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-500 mb-1">연결 명세</span>
              <span className="text-sm text-gray-900">
                {testCase.specification
                  ? `${testCase.specification.functionCode} ${testCase.specification.title}`
                  : '-'}
              </span>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-500 mb-1">생성일</span>
              <span className="text-sm text-gray-900">{new Date(testCase.createdAt).toLocaleDateString('ko-KR')}</span>
            </div>
          </div>

          {/* Editable fields */}
          {isEditing ? (
            <>
              {/* 제목 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">제목</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>

              {/* 유형 + 우선순위 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">유형</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value as typeof type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">우선순위</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 사전조건 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">사전조건</label>
                <textarea
                  value={precondition}
                  onChange={(e) => setPrecondition(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </div>

              {/* 테스트 단계 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-500">테스트 단계</label>
                  <button
                    onClick={addStep}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                  >
                    <Plus className="size-3" />
                    단계 추가
                  </button>
                </div>
                <div className="space-y-2">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded-lg p-3">
                      <span className="mt-2 text-sm font-mono text-gray-400 w-6 flex-shrink-0 text-center">
                        {step.step}
                      </span>
                      <input
                        ref={(el) => { stepInputRefs.current[idx] = el; }}
                        type="text"
                        value={step.action}
                        onChange={(e) => updateStep(idx, e.target.value)}
                        onKeyDown={(e) => handleStepKeyDown(e, idx)}
                        placeholder="동작 (Action)"
                        className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                      />
                      <button
                        onClick={() => removeStep(idx)}
                        disabled={steps.length <= 1}
                        className="mt-2 p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="size-3.5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 기대 결과 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">기대 결과</label>
                <textarea
                  value={expectedResult}
                  onChange={(e) => setExpectedResult(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </div>
            </>
          ) : (
            <>
              {/* Detail view (read mode) */}
              <div>
                <span className="block text-sm font-medium text-gray-500 mb-1">제목</span>
                <p className="text-sm text-gray-900">{testCase.title}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-1">유형</span>
                  <p className="text-sm text-gray-900">{TYPE_LABEL[testCase.type] ?? testCase.type}</p>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-1">우선순위</span>
                  <p className="text-sm text-gray-900">{PRIORITY_LABEL[testCase.priority] ?? testCase.priority}</p>
                </div>
              </div>

              {testCase.precondition && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-1">사전조건</span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{testCase.precondition}</p>
                </div>
              )}

              {testCase.steps && testCase.steps.length > 0 && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-2">테스트 단계</span>
                  <div className="space-y-2">
                    {testCase.steps.map((step, idx) => (
                      <div key={idx} className="flex gap-2 bg-gray-50 rounded-lg p-3">
                        <span className="text-sm font-mono text-gray-400 w-6 flex-shrink-0 text-center mt-0.5">
                          {step.step}
                        </span>
                        <p className="flex-1 text-sm text-gray-900">{step.action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {testCase.expectedResult && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-1">기대 결과</span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{testCase.expectedResult}</p>
                </div>
              )}

              {/* 검토 이력 */}
              {!testCase.needsReview && testCase.reviewer && testCase.reviewedAt && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <CheckCircle className="size-4 text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-800">
                    <span className="font-medium">{testCase.reviewer.name}</span>
                    님이{' '}
                    {new Date(testCase.reviewedAt).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    에 검토완료 처리
                  </p>
                </div>
              )}

              {/* 재검토 필요 뱃지 */}
              {testCase.needsReview && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  <span className="text-sm text-amber-800 font-medium">⚠ 재검토 필요</span>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </div>
  );
}
